/**
 * 既存の物件を物件マスタへ紐付ける計画を立てる純関数。
 *
 * 名前一本で確定してよいのは**マスタ内で一意なとき**だけ。同名の別ビルが
 * 71名義実在する (協和ビル = 台東区上野 / 千代田区六番町) ため、複数該当は
 * 機械的に決めず「要確認」として人に返す。
 *
 * 住所での絞り込みはしない。マスタ側の住所は22%が未入力で、アプリ側の住所は
 * 郵便番号つき等の表記ゆれがあるため、絞り込むと取りこぼしのほうが増える。
 */
import type { BuildingMasterEntry } from '@/types/building';
import { normalizeFacilityName } from '@/lib/knowledge/normalize';

/** 紐付け対象 (V1/V2 いずれの物件もこの形に落として渡す)。 */
export interface LinkTarget {
  id: string;
  name: string;
  hubspotPropertyId?: string;
}

export interface LinkPlan {
  /** 一意に決まったので紐付けられるもの。 */
  toLink: Array<{ target: LinkTarget; entry: BuildingMasterEntry }>;
  /** 既に紐付け済み (触らない)。 */
  alreadyLinked: LinkTarget[];
  /** 同名がマスタに複数あり機械的に決められないもの。 */
  ambiguous: Array<{ target: LinkTarget; candidates: BuildingMasterEntry[] }>;
  /** マスタに見つからないもの。 */
  unmatched: LinkTarget[];
}

export function planPropertyLinks(
  targets: LinkTarget[],
  entries: BuildingMasterEntry[],
): LinkPlan {
  const byName = new Map<string, BuildingMasterEntry[]>();
  for (const e of entries) {
    const k = normalizeFacilityName(e.name);
    if (!k) continue;
    const list = byName.get(k);
    if (list) list.push(e);
    else byName.set(k, [e]);
  }

  const plan: LinkPlan = {
    toLink: [],
    alreadyLinked: [],
    ambiguous: [],
    unmatched: [],
  };
  for (const t of targets) {
    if (t.hubspotPropertyId) {
      plan.alreadyLinked.push(t);
      continue;
    }
    const hits = byName.get(normalizeFacilityName(t.name)) ?? [];
    if (hits.length === 1) plan.toLink.push({ target: t, entry: hits[0]! });
    else if (hits.length > 1) plan.ambiguous.push({ target: t, candidates: hits });
    else plan.unmatched.push(t);
  }
  return plan;
}
